import React, { useState } from 'react';
import { useIntl } from 'react-intl';

import { FormField } from '~/eds/src/components/FormField/FormField';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { RadioGroup } from '~/eds/src/components/RadioGroup/RadioGroup';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { api } from '~/redux/api/createApi';

import { BasePartyModalProps, Party } from '../types';

export const AddPartyModal = ({ onHide, clientId }: BasePartyModalProps) => {
  const { toast } = useToast();
  const intl = useIntl();

  const [addParty, { isLoading }] = api.endpoints.addParty.useMutation();

  const initialParty = {
    id: 0,
    name: '',
    isInternal: true,
  };

  const [newParty, setNewParty] = useState<Party>(initialParty);

  const children = (
    <Layout preset="form-fields">
      <RadioGroup
        name="Add Party"
        title={intl.formatMessage({
          id: 'AddPartyModal.selectPartyTitle',
          defaultMessage: 'Select Party',
          description: 'Title for party selection radio group',
        })}
        options={[
          {
            label: intl.formatMessage({
              id: 'AddPartyModal.internalPartiesLabel',
              defaultMessage: 'Internal Parties',
              description: 'Label for internal parties option',
            }),
            value: true,
            info: intl.formatMessage({
              id: 'AddPartyModal.internalPartiesInfo',
              defaultMessage: 'Add a party to you internal list.',
              description: 'Information text for internal parties option',
            }),
          },
          {
            label: intl.formatMessage({
              id: 'AddPartyModal.counterPartiesLabel',
              defaultMessage: 'Counter Parties',
              description: 'Label for counter parties option',
            }),
            value: false,
            info: intl.formatMessage({
              id: 'AddPartyModal.counterPartiesInfo',
              defaultMessage: 'Add a party to your counter list.',
              description: 'Information text for counter parties option',
            }),
          },
        ]}
        onChange={(value: boolean | null) => {
          setNewParty({ ...newParty, isInternal: value! });
        }}
        value={newParty.isInternal}
      />
      <FormField<string, false>
        label={intl.formatMessage({
          id: 'AddPartyModal.addNewPartyLabel',
          defaultMessage: 'Add New Party',
          description: 'Label for new party input field',
        })}
        input={TextInput}
        name="Edit Party Name"
        value={newParty.name}
        onChange={(updatedPartyName) =>
          setNewParty({ ...newParty, name: updatedPartyName ?? '' })
        }
      />
    </Layout>
  );

  return (
    <Modal
      children={children}
      enableHideOnPrimaryAction={true}
      isVisible={true}
      primaryAction={{
        isLoading,
        text: intl.formatMessage({
          id: 'AddPartyModal.addPartyButton',
          defaultMessage: 'Add Party',
          description: 'Primary action button text',
        }),
        disabled: !newParty.name,
        tooltip: !newParty.name
          ? intl.formatMessage({
              id: 'AddPartyModal.enterPartyNameTooltip',
              defaultMessage: 'Please enter a party name to continue.',
              description: 'Tooltip when no party name is entered',
            })
          : intl.formatMessage({
              id: 'AddPartyModal.addPartyTooltip',
              defaultMessage: 'Add your Party',
              description: 'Tooltip when ready to add party',
            }),
        onClick: () => {
          addParty({
            party: newParty,
            clientId,
          })
            .unwrap()
            .then(() => {
              toast({
                message: intl.formatMessage(
                  {
                    id: 'AddPartyModal.successMessage',
                    defaultMessage: 'Party "{partyName}" has been added.',
                    description:
                      'Success message when party is added. Example: partyName = "ABC Corporation"',
                  },
                  {
                    partyName: newParty.name,
                  },
                ),
                status: 'success',
              });
            })
            .catch((error) => {
              toast({
                message: `${error.response.data.detail}`,
                status: 'danger',
              });
            });
          onHide();
        },
        variant: 'primary',
      }}
      onHide={onHide}
      title={intl.formatMessage({
        id: 'AddPartyModal.modalTitle',
        defaultMessage: 'New Party List',
        description: 'Modal title for adding new party',
      })}
    />
  );
};
